import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Heading, Paragraph, Text, Anchor } from "@zillow/constellation-10";
import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { getCommaSeparatedNumber, commaSeparateNumber } from "../../utils/zhl-calculator.esm.js";
import TextField from "../Fields/TextField.esm.js";
import { triggerFormFieldEngagementClickstreamEvent } from "./HomeSaleProceedsCalculater.clickstream.esm.js";
import { FieldConfig, validateAndTrackOptionsBlock } from "./HomeSaleProceedsCalculator.utils.esm.js";
const tooltipParagraphCss = {
  marginRight: "{spacing.default}"
};
const TOOLTIPS = {
  homeImprovement: {
    title: "Home improvement",
    body: /* @__PURE__ */ jsxs(Paragraph, { css: tooltipParagraphCss, children: [
      /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/research/sellers-housing-trends-report-2024-34385/", target: "_blank", children: "Seventy-two percent of sellers" }),
      " ",
      "make at least one improvement. Common improvements include: kitchen updates, bathroom improvement, replacing or purchasing new appliances, landscaping, replacing or repairing flooring, and roof repairs."
    ] })
  },
  cleaningAndPrep: {
    title: "Cleaning and prep",
    body: /* @__PURE__ */ jsx(Paragraph, { css: tooltipParagraphCss, children: "Professional home preparation, including: home staging, cleaning, interior and exterior painting, full-service lawn care, and carpet cleaning." })
  },
  monthlyMortgagePayments: {
    title: "Monthly mortgage payments",
    body: /* @__PURE__ */ jsx(Paragraph, { css: tooltipParagraphCss, children: "While a home is on the market, sellers continue paying their monthly mortgage payment up until closing." })
  },
  moving: {
    title: "Moving",
    body: /* @__PURE__ */ jsx(Paragraph, { css: tooltipParagraphCss, children: "Relocating to temporary housing or to a new home will cost moving and often storage expenses out-of-pocket, prior to receiving your sale proceeds." })
  },
  agentCommission: {
    title: "Agent commission",
    body: /* @__PURE__ */ jsxs(Paragraph, { css: tooltipParagraphCss, children: [
      "Sellers typically pay both their listing agent and the buyer agent's commissions, which come out to be an average of 2-3% per agent. Agent fees are negotiable at the onset of your partnership.",
      " ",
      /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/research/cost-selling-house-tax-commission-23717/", children: "Learn more" })
    ] })
  },
  sellingConcessions: {
    title: "Selling concessions",
    body: /* @__PURE__ */ jsxs(Paragraph, { css: tooltipParagraphCss, children: [
      /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/learn/seller-concessions/", children: "Seller concessions" }),
      " ",
      "are items the seller agrees to pay for on behalf of the buyer at closing. Common examples include a seller agreeing to cover part of a buyer's closing costs, or offering to cover the cost of needed repairs uncovered during the home inspection. While seller concessions are common, they're not included in every offer."
    ] })
  },
  closingFees: {
    title: "Closing fees",
    body: /* @__PURE__ */ jsx(Paragraph, { css: tooltipParagraphCss, children: "Home sale transaction costs include title insurance, escrow fees, possible attorney fees, and HOA fees which will be prorated based on your closing date." })
  },
  taxes: {
    title: "Taxes",
    body: /* @__PURE__ */ jsx(Paragraph, { css: tooltipParagraphCss, children: "Sellers pay local transfer taxes that vary based on city and state. Property taxes will be prorated with your closing date and you may owe capital gains tax after the transaction." })
  }
};
const HomeSaleProceedsFields = () => {
  const {
    setValue,
    getValues,
    watch
  } = useFormContext();
  const [rightAdornments, setRightAdornments] = useState({
    homeImprovement: 0,
    cleaningAndPrep: 1.9867,
    monthlyMortgagePayments: 0,
    moving: 0.1657,
    agentCommission: 6,
    sellingConcessions: 1,
    closingFees: 0.5,
    taxes: 1
  });
  useEffect(() => {
    const {
      unsubscribe
    } = watch((data, info) => {
      if (info.name !== FieldConfig.desiredSellingPrice.name) {
        return;
      }
      const newValues = {};
      const desiredSellingPrice = data.desiredSellingPrice || 0;
      Object.entries(rightAdornments).forEach(([key, percentage]) => {
        var _a;
        const fieldName = (_a = FieldConfig[key]) == null ? void 0 : _a.name;
        if (fieldName) {
          newValues[fieldName] = Math.round(desiredSellingPrice * percentage / 100);
        }
      });
      Object.entries(newValues).forEach(([field, value]) => {
        setValue(field, getCommaSeparatedNumber(value), validateAndTrackOptionsBlock);
      });
    });
    return () => unsubscribe();
  }, [setValue]);
  const handleManualFieldChange = (name) => (event) => {
    const inputValue = event.target.value.trim().replace(/,/g, "");
    const newValue = inputValue === "" ? 0 : Number(inputValue);
    setValue(name, getCommaSeparatedNumber(newValue), validateAndTrackOptionsBlock);
    let result = newValue / getValues().desiredSellingPrice * 100;
    if (!isFinite(result) || isNaN(result)) {
      result = 0;
    }
    setRightAdornments((prev) => ({
      ...prev,
      [name]: parseFloat(result.toFixed(2))
    }));
  };
  const handleEmptyDesireSellingPriceField = (name) => (event) => {
    const inputValue = event.target.value.trim().replace(/,/g, "");
    if (inputValue === "") {
      setValue(name, 0, validateAndTrackOptionsBlock);
    }
  };
  const setRightAdornmentValue = (number) => Number.isInteger(number) ? number + " %" : number.toFixed(2) + " %";
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsx(Heading, { level: 5, css: {
      marginBottom: "{spacing.default}"
    }, "aria-live": "polite", "aria-atomic": "true", children: "Selling price & mortgage" }),
    /* @__PURE__ */ jsx(Paragraph, { css: {
      paddingBottom: "{spacing.default}"
    }, children: "The amount you'd like to sell your home for and total remaining mortgage amount." }),
    /* @__PURE__ */ jsx(TextField, { label: "Desired selling price", leftAdornment: "$", name: FieldConfig.desiredSellingPrice.name, config: FieldConfig.desiredSellingPrice.config, autoComplete: "off", format: commaSeparateNumber, onBlur: () => triggerFormFieldEngagementClickstreamEvent("Desired selling price"), onChange: handleEmptyDesireSellingPriceField(FieldConfig.desiredSellingPrice.name), required: true }),
    /* @__PURE__ */ jsx(TextField, { label: "Remaining mortgage owed", leftAdornment: "$", name: FieldConfig.remainingMortgageOwed.name, config: FieldConfig.remainingMortgageOwed.config, autoComplete: "off", format: commaSeparateNumber, onBlur: () => triggerFormFieldEngagementClickstreamEvent("Remaining mortgage owed"), onChange: handleManualFieldChange(FieldConfig.remainingMortgageOwed.name) }),
    /* @__PURE__ */ jsx(Heading, { level: 5, css: {
      marginBottom: "{spacing.tighter}"
    }, "aria-live": "polite", "aria-atomic": "true", children: "Estimated prep & repair costs" }),
    /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", css: {
      marginBottom: "{spacing.tighter}"
    }, asChild: true, children: /* @__PURE__ */ jsx("p", { children: "Average 35 days" }) }),
    /* @__PURE__ */ jsx(Paragraph, { css: {
      paddingBottom: "{spacing.default}"
    }, children: "All the work you need to complete before you're ready to list." }),
    /* @__PURE__ */ jsx(TextField, { label: "Home improvement", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.homeImprovement), name: FieldConfig.homeImprovement.name, config: FieldConfig.homeImprovement.config, onChange: handleManualFieldChange(FieldConfig.homeImprovement.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Home improvement"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.homeImprovement }),
    /* @__PURE__ */ jsx(TextField, { label: "Cleaning and prep", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.cleaningAndPrep), name: FieldConfig.cleaningAndPrep.name, config: FieldConfig.cleaningAndPrep.config, onChange: handleManualFieldChange(FieldConfig.cleaningAndPrep.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Cleaning and prep"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.cleaningAndPrep }),
    /* @__PURE__ */ jsx(Heading, { level: 5, css: {
      marginBottom: "{spacing.tighter}"
    }, "aria-live": "polite", "aria-atomic": "true", children: "Estimated on-market costs" }),
    /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", css: {
      marginBottom: "{spacing.tighter}"
    }, asChild: true, children: /* @__PURE__ */ jsx("p", { children: "Average 68 days" }) }),
    /* @__PURE__ */ jsx(Paragraph, { css: {
      paddingBottom: "{spacing.default}"
    }, children: "The out-of-pocket costs of transitioning your life from one home to another." }),
    /* @__PURE__ */ jsx(TextField, { label: "Monthly mortgage payments", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.monthlyMortgagePayments), name: FieldConfig.monthlyMortgagePayments.name, config: FieldConfig.monthlyMortgagePayments.config, onChange: handleManualFieldChange(FieldConfig.monthlyMortgagePayments.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Monthly mortgage payments"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.monthlyMortgagePayments }),
    /* @__PURE__ */ jsx(TextField, { label: "Moving", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.moving), name: FieldConfig.moving.name, config: FieldConfig.moving.config, onChange: handleManualFieldChange(FieldConfig.moving.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Moving"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.moving }),
    /* @__PURE__ */ jsx(Heading, { level: 5, css: {
      marginBottom: "{spacing.tighter}"
    }, "aria-live": "polite", "aria-atomic": "true", children: "Estimated closing costs" }),
    /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", css: {
      marginBottom: "{spacing.tighter}"
    }, asChild: true, children: /* @__PURE__ */ jsx("p", { children: "Average 45 days" }) }),
    /* @__PURE__ */ jsx(Paragraph, { css: {
      paddingBottom: "{spacing.default}"
    }, children: "The standard costs of the home sale transaction, paid at closing." }),
    /* @__PURE__ */ jsx(TextField, { label: "Agent commission", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.agentCommission), name: FieldConfig.agentCommission.name, config: FieldConfig.agentCommission.config, onChange: handleManualFieldChange(FieldConfig.agentCommission.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Agent commission"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.agentCommission }),
    /* @__PURE__ */ jsx(TextField, { label: "Selling concessions", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.sellingConcessions), name: FieldConfig.sellingConcessions.name, config: FieldConfig.sellingConcessions.config, onChange: handleManualFieldChange(FieldConfig.sellingConcessions.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Selling concessions"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.sellingConcessions }),
    /* @__PURE__ */ jsx(TextField, { label: "Closing fees", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.closingFees), name: FieldConfig.closingFees.name, config: FieldConfig.closingFees.config, onChange: handleManualFieldChange(FieldConfig.closingFees.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Closing fees"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.closingFees }),
    /* @__PURE__ */ jsx(TextField, { label: "Taxes", leftAdornment: "$", rightAdornment: setRightAdornmentValue(rightAdornments.taxes), name: FieldConfig.taxes.name, config: FieldConfig.taxes.config, onChange: handleManualFieldChange(FieldConfig.taxes.name), onBlur: () => triggerFormFieldEngagementClickstreamEvent("Taxes"), autoComplete: "off", format: commaSeparateNumber, tooltip: TOOLTIPS.taxes })
  ] });
};
export {
  HomeSaleProceedsFields as default
};
